import { useState } from 'react';
import { useAuthCtx } from '../lib/auth-context';
import { Modal, Toaster } from './ui';
import { LogoMark } from './LogoMark';

/**
 * First-run landing (design.html welcome screen). Shown instead of the app
 * chrome while nobody is signed in and the visitor hasn't chosen "browse as
 * guest" yet. Google button signs in; the guest button unlocks the hub for
 * this device (progress kept in localStorage).
 */
export const GUEST_ENTRY_KEY = 'msk.guest-entry';

function enterAsGuest() {
  localStorage.setItem(GUEST_ENTRY_KEY, '1');
  window.location.reload();
}

export function Welcome() {
  const auth = useAuthCtx();
  const [nudge, setNudge] = useState(false);

  return (
    <div className="welcome">
      <div className="w-orb g" aria-hidden />
      <div className="w-orb p" aria-hidden />
      <div className="w-orb a" aria-hidden />

      <div className="welcome-inner">
        <div className="welcome-copy">
          <div className="welcome-brand">
            <span className="logo" aria-hidden><LogoMark /></span>
            <span className="welcome-eyebrow">MasterStax · Student Resources Hub</span>
          </div>

          <h1 className="welcome-title">
            Your courses,<br />chapter by chapter.
          </h1>
          <p className="welcome-lede">
            Lecture videos and worked notes for Calculus 1 —
            organised into chapters and sections, with your progress saved as you go.
          </p>

          <div className="welcome-actions">
            <button className="wbtn google" onClick={() => void auth.signInWithGoogle()}>
              <span className="g-icon" aria-hidden><span /></span>
              Continue with Google
            </button>
            <button className="wbtn dark" onClick={() => setNudge(true)}>
              Browse as guest
            </button>
          </div>
          <p className="welcome-note">Guest mode keeps progress on this device only.</p>

          <div className="welcome-pills">
            <span className="pill"><i style={{ background: '#2FBF6D' }} />100% free</span>
            <span className="pill"><i style={{ background: '#9B3F86' }} />Built on OpenStax</span>
            <span className="pill"><i style={{ background: '#F0A800' }} />Free accounts, forever</span>
          </div>
          <p className="welcome-madeby">
            MasterStax is a{' '}
            <a href="https://mikalabs.org" target="_blank" rel="noopener noreferrer">MikaLabs</a> product.
          </p>
        </div>

        <div className="welcome-art" aria-hidden>
          <div className="w-card a">
            <div className="w-card-head">
              <span className="w-glyph" style={{ background: 'var(--t-green)', color: 'var(--c-green)' }}>∂</span>
              <span className="w-code">MATH 151</span>
            </div>
            <div className="w-card-name">Calculus 1</div>
            <div className="w-bar"><div className="w-fill" style={{ width: '19%', background: '#2FBF6D' }} /></div>
            <div className="w-foot"><span>6 chapters</span><span>19%</span></div>
          </div>

          <div className="w-card b">
            <div className="w-card-head">
              <span className="w-glyph" style={{ background: 'var(--t-plum)', color: 'var(--c-plum)' }}>ƒ</span>
              <span className="w-code">OPENSTAX</span>
            </div>
            <div className="w-card-name">Calculus Volume 1</div>
            <div className="w-bar"><div className="w-fill" style={{ width: '100%', background: '#9B3F86' }} /></div>
            <div className="w-foot"><span>chapters 1—6</span><span>free</span></div>
          </div>

          <div className="w-resume">
            <span className="w-play">▶</span>
            <span>
              <span className="w-resume-name">The Chain Rule</span>
              <span className="w-resume-sub">resume · 4:12 left</span>
            </span>
          </div>

          <div className="w-streak"><i />12-day streak</div>
        </div>
      </div>

      {nudge && <AccountNudge onClose={() => setNudge(false)} />}
      <Toaster />
    </div>
  );
}

function AccountNudge({ onClose }: { onClose: () => void }) {
  const auth = useAuthCtx();
  return (
    <Modal title="Before you browse — one quick idea" onClose={onClose}>
      <p style={{ color: 'var(--ink2)', fontSize: 15, lineHeight: 1.65, margin: '0 0 16px' }}>
        MasterStax works without an account, but signing up is free forever and the whole
        experience follows you around. It takes about ten seconds with Google:
      </p>
      <div className="nudge-list">
        <span className="nudge-row"><i>✓</i>Progress synced on every device you sign in on</span>
        <span className="nudge-row"><i>✓</i>Daily streaks, XP and study levels</span>
        <span className="nudge-row"><i>✓</i>Nothing lost when you change phone or clear your browser</span>
      </div>
      <div className="nudge-foot">
        <button className="btn btn-dark" style={{ justifyContent: 'center' }} onClick={() => void auth.signInWithGoogle()}>
          Start free account — free forever
        </button>
        <button className="ghost" onClick={enterAsGuest}>No thanks, browse as guest</button>
      </div>
    </Modal>
  );
}
